/**
 * **부트스트랩 설정.** 앱이 서버에 말을 걸기 전에 알아야 하는 것만 여기 있다.
 *
 * ── B8 이 이 파일을 반으로 갈랐다 ──────────────────────────────
 *
 * 모드·VAPID 공개키·구글 클라이언트 ID·TV2M 주소·TV2M 앱 키는 **빌드 시점이 아니라
 * `GET /v1/bloom/config` 로 받는다** (`runtime-config.ts`, `docs/12` §2.4). 값이 두 곳에
 * 있으면 어긋나고 **어긋난 증상이 조용하기 때문이다** — VAPID 공개키가 실제로 그랬다
 * (서버와 번들에 각각 있어, 어긋나면 권한은 허용되는데 구독만 조용히 실패한다).
 *
 * 그래서 남는 것은 아래뿐이다. **전부 「서버가 정할 값이 아닌 것」이다.**
 *
 * 1. `serverUrl` — **서버에 물어보려면 서버 주소는 알아야 한다.** 이 하나만 빌드 시점에
 *    박히는 것이 계약이고(`docs/13` §6 — "프런트에 남는 것은 `VITE_SERVER_URL` 하나"),
 *    나머지는 그 주소로 받아 온다.
 * 2. `channelId` — **v9 에서 뜻이 바뀌었다.** 「앱이 트는 채널」이 아니라 **「첫 진입 시
 *    기본으로 고를 채널」**이고, **비어 있으면 채널 목록부터 그린다** (`docs/23` §7.2).
 *    채널이 둘 이상이 된 뒤로는 앱이 하나를 미리 알 이유가 없다 — 서버가 공개 목록을 준다.
 * 3. `debug` — 계측 여부. 서버가 정할 값이 아니다.
 * 4. `enableModeASim`·`simCampaignId` (B11) — **BLOOM 이 아닌 화면**을 여는 스위치다.
 *    BLOOM Server 가 그것을 알 이유가 없다.
 *
 * 쿼리 파라미터가 환경변수를 덮는 규칙은 그대로 둔다 — 한 브라우저에서 채널·서버를
 * 바꿔가며 확인하는 것이 M6·M7·T7 검증의 기본 동작이고, 그때마다 빌드를 다시 하고 싶지 않다.
 */

import { isAppWindow, readAppDisplay } from './client-type.js';
import { parseRequestedEngine } from './receiver-engine.js';

const params = new URLSearchParams(window.location.search);

/** 기본값이 없는 값. 쿼리도 환경변수도 비어 있으면 `null` 이다. */
function pickOptional(queryKey: string, envValue: string | undefined): string | null {
  const fromQuery = params.get(queryKey);
  if (fromQuery !== null && fromQuery !== '') return fromQuery;
  if (envValue !== undefined && envValue !== '') return envValue;
  return null;
}

function pick(queryKey: string, envValue: string | undefined, fallback: string): string {
  const fromQuery = params.get(queryKey);
  if (fromQuery !== null && fromQuery !== '') return fromQuery;
  if (envValue !== undefined && envValue !== '') return envValue;
  return fallback;
}

export const config = {
  /**
   * BLOOM Server. 플레이리스트·이벤트·HLS 가 여기로 가고, **상품 화면의 제목·가격·썸네일도
   * 여기서 읽는다** — 매칭은 TV2M 이 하지만 상품은 BLOOM Server 가 갖고 있기 때문이다
   * (`docs/07` §3.1, T9 의 공개 단건 조회). **런타임 설정도 여기서 받는다.**
   */
  serverUrl: pick('server', import.meta.env.VITE_SERVER_URL, 'http://localhost:3000'),

  /**
   * **첫 진입 시 기본으로 고를 채널. 비어 있으면 `null` 이고 그때는 목록부터 그린다.**
   *
   * 기본값을 두지 않는다 — 시드의 채널 UUID 를 박아 두면 그 채널이 편성이 비어 공개
   * 목록에서 빠진 배포에서 **빈 화면**이 된다. 판정은 `channel-selection.ts` 가 하고,
   * 거기서 목록에 없는 값은 버려진다.
   *
   * **`?channel=` 은 저장하지 않는다** — 하네스와 데모 운영자가 잠시 고정해 보는 통로다
   * (`?lang=` 과 같은 방침). 사람이 화면에서 고른 채널만 `bloom.channelId` 에 남는다.
   */
  channelId: pickOptional('channel', import.meta.env.VITE_CHANNEL_ID),

  /**
   * `?debug=1` — 시청 화면에서는 계측 패널을, 그 밖의 경로에서는 진단 화면을 연다
   * (`routing.ts`). 기본은 꺼짐이다: 패널 자체가 메인 스레드를 쓰므로 켜 두면 전환
   * 측정값이 바뀐다.
   */
  debug: params.get('debug') === '1',

  /**
   * STB 모사(`/sim/stb`) 노출 여부 (B11, `docs/13` §6).
   *
   * **BLOOM 의 화면이 아니다.** 다른 사업자의 셋톱 앱을 흉내 내어 위탁형(Mode A) 경로를
   * 눌러 보는 자리이고, 그래서 배포 설정으로 가린다.
   *
   * `?sim=1` 로도 켠다. 실기 확인에서 이 화면 하나 때문에 번들을 다시 만들지 않기
   * 위함이고, **가려도 잃을 것이 없어서다** — 여기서 하는 일은 무인증 `/v1/trigger` 호출
   * 하나이고 그 경로는 어차피 공개다 (파트너 기기가 키를 실을 수 없다, T3).
   * 로그인 게이트처럼 뒤집히면 안 되는 값이 아니다.
   *
   * 광고 캠페인은 `?campaign=` 으로 고른다 (기본값은 시드의 첫 소재).
   */
  enableModeASim: params.get('sim') === '1' || import.meta.env.VITE_ENABLE_MODE_A_SIM === 'true',

  /**
   * STB 모사가 트는 광고. **시드의 첫 소재이고 앱이 아는 유일한 카탈로그 값이다** —
   * 나머지는 전부 `GET /v1/creatives/{campaignId}` 가 준다.
   */
  simCampaignId: pick('campaign', undefined, 'ad-001'),

  /**
   * v36 — **`?engine=flower` 면 Flower 엔진을 고른다**(절대 규칙 93 · `docs/72` §2). 고른 것일 뿐이고 실제 엔진은
   * `receiverEngineFor()` 가 정한다(네이티브 HLS 면 언제나 BLOOM). **저장하지 않는다** — 문서를 처음 연 URL 의 값이
   * 그 페이지가 사는 동안의 선택이다(`localStorage` 금지 · 지시서 §8). 모르는 값은 고르지 않은 것과 같다.
   */
  requestedEngine: parseRequestedEngine(params.get('engine')),

  /**
   * v38 — **문서가 설 때 설치된 앱의 창이었는가**(`isAppWindow`). 참이면 개인화 채널의 기본 엔진이 Flower 다
   * (`receiverEngineFor` · 절대 규칙 93). `requestedEngine` 처럼 **페이지가 사는 동안 한 번** 읽는다 — 리시버를 만들 때마다
   * 읽으면 채널 전환마다 판정이 다시 서고, 창 모양은 그 사이 바뀐다(첫 조작의 전체화면). 저장하지 않는다.
   */
  appWindowAtLoad: isAppWindow(readAppDisplay()),
} as const;

/**
 * 런타임 설정을 덮는 쿼리 파라미터. **하네스와 실기 확인의 통로다** — 서버를 다시 띄우지
 * 않고 TV2M 주소나 VAPID 키를 바꿔 볼 수 있어야 한다.
 *
 * 여기 없는 것(`mode`·`requireLogin`·`googleClientId`)은 **덮을 수 없다.** 배포 정책이고,
 * 브라우저가 뒤집을 수 있으면 로그인 게이트가 쿼리 하나로 열린다.
 */
export const runtimeOverrides = {
  tv2mServerUrl: params.get('tv2m'),
  tv2mApiKey: params.get('key'),
  vapidPublicKey: params.get('vapid'),
} as const;

/**
 * 서버의 `Device.appId` 로 그대로 간다. 네이티브 포팅에서 이 값이 갈린다.
 *
 * **B7 에서 `mobile-web` → `bloom` 으로 바뀌었다.** 앱이 하나가 됐고, 이 값으로 기기를
 * 고르는 코드는 없다(등록·조회 어디에도 조건이 아니다) — 기록에만 남는다.
 */
export const APP_ID = 'bloom';
